import React, { useState, useEffect, useCallback, useContext } from 'react';
import api from '../api/api';
import { SnackbarContext } from '../App';
import { useConfirm } from '../contexts/ConfirmContext';
import ContactCreateModal from './ContactCreateModal';
import {
    Sheet,
    SheetContent,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import {
    Globe,
    MapPin,
    Mail,
    Plus,
    X,
    ExternalLink,
    Edit3,
    Contact2,
    History,
    ChevronLeft,
    Info
} from "lucide-react";
import { format } from 'date-fns';

export default function CompanyDetailDrawer({ isOpen, onClose, companyId, onSaveSuccess, onContactClick }) {
    const showSnackbar = useContext(SnackbarContext);
    const openConfirm = useConfirm();
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isAddLeadOpen, setIsAddLeadOpen] = useState(false);
    const [form, setForm] = useState({
        name: '',
        domain: '',
        website: '',
        linkedinUrl: '',
        industry: '',
        employeeSize: '',
        location: '',
        notes: ''
    });

    const fetchCompanyDetail = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/api/crm/companies/${companyId}`);
            setCompany(response.data);
            setForm({
                name: response.data.name || '',
                domain: response.data.domain || '',
                website: response.data.website || '',
                linkedinUrl: response.data.linkedinUrl || '',
                industry: response.data.industry || '',
                employeeSize: response.data.employeeSize || '',
                location: response.data.location || '',
                notes: response.data.notes || ''
            });
        } catch (error) {
            console.error('Error fetching company detail:', error);
            showSnackbar('Error loading company details', 'error');
        } finally {
            setLoading(false);
        }
    }, [companyId, showSnackbar]);

    useEffect(() => {
        if (isOpen && companyId) {
            fetchCompanyDetail();
        } else {
            setCompany(null);
            setIsEditing(false);
        }
    }, [isOpen, companyId, fetchCompanyDetail]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put(`/api/crm/companies/${companyId}`, form);
            showSnackbar('Company updated successfully', 'success');
            setIsEditing(false);
            fetchCompanyDetail();
            if (onSaveSuccess) onSaveSuccess();
        } catch (error) {
            console.error('Error updating company:', error);
            showSnackbar('Error updating company', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        openConfirm({
            title: 'Delete Account?',
            message: 'Are you sure you want to permanently delete this account? All associated contacts will remain safely inside your workspace, but will be unlinked from this company.',
            confirmText: 'Delete',
            variant: 'destructive',
            onConfirm: async () => {
                try {
                    await api.delete(`/api/crm/companies/${companyId}`);
                    showSnackbar('Account deleted and contacts unlinked', 'success');
                    onClose();
                    if (onSaveSuccess) onSaveSuccess();
                } catch (error) {
                    showSnackbar(error.isAppError ? error.message : 'Error archiving account', 'error');
                }
            }
        });
    };

    if (!isOpen) return null;

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent size="xl" className="p-0 h-full flex flex-col">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground animate-in fade-in">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm font-medium">Loading organization...</p>
                    </div>
                ) : company ? (
                    <>
                        <div className="p-6 border-b border-border bg-muted/20 backdrop-blur-md sticky top-0 z-10">
                            {onContactClick && (
                                <button onClick={onClose} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-muted-foreground/70 w-fit mb-4 cursor-pointer hover:text-primary transition-colors hover:bg-primary/5 px-2 py-1 -ml-2 rounded-lg">
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                    Back to Contacts
                                </button>
                            )}
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold text-2xl uppercase shadow-glow">
                                        {company.name.substring(0, 1)}
                                    </div>
                                    <div className="space-y-0.5">
                                        <h2 className="text-xl font-bold tracking-tight text-foreground">{company.name}</h2>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 text-[10px] font-black uppercase tracking-widest px-1.5 h-4.5">
                                                Workspace Account
                                            </Badge>
                                            {company.domain && (
                                                <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5 border-l border-border pl-2">
                                                    <Globe className="w-3 h-3 opacity-50" />
                                                    {company.domain}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-1.5">
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setIsEditing(!isEditing)}>
                                        <Edit3 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <ScrollArea className="flex-1 overflow-y-auto">
                            <div className="p-6 space-y-8 pb-20">
                                {/* Core Information */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Info className="w-4 h-4 text-primary" />
                                        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground/70">Account Profile</h3>
                                    </div>
                                    
                                    {isEditing ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-xl border border-border/50 shadow-inner">
                                            <div className="space-y-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Name</Label>
                                                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Domain</Label>
                                                <Input value={form.domain} onChange={e => setForm({...form, domain: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Website</Label>
                                                <Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">LinkedIn URL</Label>
                                                <Input value={form.linkedinUrl} onChange={e => setForm({...form, linkedinUrl: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Industry</Label>
                                                <Input value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Employee Size</Label>
                                                <Input value={form.employeeSize} onChange={e => setForm({...form, employeeSize: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2 sm:col-span-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Location</Label>
                                                <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="h-9 bg-background focus-visible:ring-primary" />
                                            </div>
                                            <div className="space-y-2 sm:col-span-2">
                                                <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Notes</Label>
                                                <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-background focus-visible:ring-primary resize-none h-24" />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 bg-card border border-border/50 p-5 rounded-2xl shadow-sm">
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Industry</p>
                                                <p className="text-sm font-bold text-foreground">{company.industry || '—'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Company size</p>
                                                <p className="text-sm font-bold text-foreground">{company.employeeSize || '—'}</p>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Location</p>
                                                <div className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                                                    <MapPin className="w-3.5 h-3.5 text-primary/60" />
                                                    {company.location || '—'}
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Website</p>
                                                <div className="flex items-center gap-2">
                                                    <a href={company.website?.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-primary hover:underline flex items-center gap-1">
                                                        {company.website || '—'}
                                                        {company.website && <ExternalLink className="w-3 h-3" />}
                                                    </a>
                                                </div>
                                            </div>
                                            <div className="sm:col-span-2 space-y-2 mt-2 pt-4 border-t border-border/50">
                                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">Internal Notes</p>
                                                <p className="text-xs text-muted-foreground leading-relaxed italic bg-muted/40 p-3 rounded-xl border border-border/30">
                                                    {company.notes || 'No account notes provided yet.'}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Linked Contacts Section */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <Contact2 className="w-4 h-4 text-primary" />
                                            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground/70">Linked Contacts</h3>
                                            <Badge className="bg-primary/10 text-primary border-0 text-[10px] font-bold h-4.5 px-1.5 ml-1">
                                                {company.contactCount || 0}
                                            </Badge>
                                        </div>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => setIsAddLeadOpen(true)}
                                            className="h-8 text-xs font-bold text-primary hover:bg-primary/5 gap-1.5 px-3 rounded-lg border border-transparent hover:border-primary/20"
                                        >
                                            <Plus className="w-3.5 h-3.5" /> Add Contact
                                        </Button>
                                    </div>

                                    <div className="bg-card border border-border/50 rounded-2xl overflow-hidden shadow-sm">
                                        <Table>
                                            <TableHeader className="bg-muted/30">
                                                <TableRow className="hover:bg-muted/30 border-b border-border/50">
                                                    <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-2.5 px-4 h-auto">Contact</TableHead>
                                                    <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-2.5 h-auto text-right pr-4">Stage</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {company.contacts && company.contacts.length > 0 ? (
                                                    company.contacts.map((contact) => (
                                                        <TableRow key={contact._id} className="group hover:bg-primary/[0.02] cursor-pointer transition-colors border-b border-border/30" onClick={() => onContactClick && onContactClick(contact._id)}>
                                                            <TableCell className="py-3.5 px-4">
                                                                <div className="flex flex-col">
                                                                    <span className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">
                                                                        {contact.firstName} {contact.lastName}
                                                                    </span>
                                                                    <span className="text-[10px] text-muted-foreground font-medium flex items-center gap-1 opacity-70">
                                                                        <Mail className="w-2.5 h-2.5" />
                                                                        {contact.email}
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="py-3.5 text-right pr-4">
                                                                <Badge className="text-[9px] font-black bg-accent/40 text-accent-foreground border-border/50 px-1.5 h-4.5">
                                                                    {contact.stage}
                                                                </Badge>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={2} className="h-20 text-center text-xs text-muted-foreground opacity-60">
                                                            No contacts linked to this account.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                                
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <History className="w-4 h-4 text-primary" />
                                        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground/70">Governance & Audit</h3>
                                    </div>
                                    <div className="bg-muted/30 border border-border/50 rounded-xl p-4 text-[10px] text-muted-foreground flex items-center justify-between">
                                        <div className="space-y-1">
                                            <p className="font-bold opacity-60 uppercase tracking-widest">Created on</p>
                                            <p className="text-foreground font-medium">{format(new Date(company.createdAt), 'MMM dd, yyyy HH:mm')}</p>
                                        </div>
                                        <div className="space-y-1 text-right">
                                            <p className="font-bold opacity-60 uppercase tracking-widest">Last Modified</p>
                                            <p className="text-foreground font-medium">{format(new Date(company.updatedAt), 'MMM dd, yyyy HH:mm')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollArea>

                        <div className="p-6 border-t border-border bg-card/80 backdrop-blur-md absolute bottom-0 w-full flex justify-between items-center z-10">
                            <Button 
                                variant="ghost" 
                                onClick={handleDelete}
                                size="sm" 
                                className="text-destructive hover:bg-destructive/10 text-xs font-bold uppercase tracking-widest rounded-xl px-4 border border-transparent hover:border-destructive/20 transition-all font-mono"
                            >
                                Delete Account
                            </Button>
                            <div className="flex gap-2">
                                {isEditing ? (
                                    <>
                                        <Button variant="outline" size="sm" className="rounded-xl px-4 text-xs font-bold uppercase tracking-widest border-border shadow-sm" onClick={() => setIsEditing(false)}>
                                            Cancel
                                        </Button>
                                        <Button size="sm" className="rounded-xl px-4 text-xs font-bold uppercase tracking-widest bg-primary hover:bg-primary/90 shadow-glow" onClick={handleSave} disabled={saving}>
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </>
                                ) : (
                                    <Button size="sm" className="rounded-xl px-4 text-xs font-bold uppercase tracking-widest bg-primary hover:bg-primary/90 shadow-glow" onClick={onClose}>
                                        Close Profile
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                ) : null}
            </SheetContent>

            <ContactCreateModal 
                isOpen={isAddLeadOpen} 
                onClose={() => setIsAddLeadOpen(false)} 
                initialCompanyId={companyId}
                initialCompanyName={company?.name}
                onSuccess={() => {
                    fetchCompanyDetail();
                    if (onSaveSuccess) onSaveSuccess();
                }}
            />
        </Sheet>
    );
}

